"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { Lock } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  LESSON_PROGRESS_COMPLETED_EVENT,
  type LessonProgressCompletedDetail,
} from "@/features/courses/constants";

type LessonRef = { slug: string; title: string } | null;
type AssessmentRef = { title: string; required: boolean } | null;
type UniversalAssessmentRef = {
  title: string;
  slug: string;
  required: boolean;
  completed: boolean;
} | null;
type TrackGroupRef = {
  id: string;
  title: string;
  isCurrentModuleTrack: boolean;
} | null;

type NextTrackGroupRef = {
  id: string;
  title: string;
} | null;

interface LessonNavigationProps {
  courseSlug: string;
  lessonSlug: string;
  isAuthenticated: boolean;
  initialCompleted: boolean;
  shouldHideNextLesson: boolean;
  prevLesson: LessonRef;
  prevModuleTitle: string | null;
  nextLesson: LessonRef;
  nextModuleTitle: string | null;
  nextLessonInModule: boolean;
  nextAssessment: AssessmentRef;
  nextAssessmentSlug: string | null;
  nextUniversalAssessment?: UniversalAssessmentRef;
  trackGroup?: TrackGroupRef;
  nextTrackGroup?: NextTrackGroupRef;
  prevTrackGroup?: NextTrackGroupRef;
  isLastLesson?: boolean;
}

export function LessonNavigation(props: LessonNavigationProps) {
  const {
    courseSlug,
    lessonSlug,
    isAuthenticated,
    initialCompleted,
    shouldHideNextLesson,
    prevLesson,
    prevModuleTitle,
    nextLesson,
    nextModuleTitle,
    nextLessonInModule,
    nextAssessment,
    nextAssessmentSlug,
    nextUniversalAssessment,
    trackGroup,
    nextTrackGroup,
    prevTrackGroup,
    isLastLesson,
  } = props;

  const [isCompleted, setIsCompleted] = useState(initialCompleted);

  useEffect(() => {
    setIsCompleted(initialCompleted);
  }, [initialCompleted]);

  useEffect(() => {
    const handleCompletion = (event: Event) => {
      const customEvent = event as CustomEvent<LessonProgressCompletedDetail>;
      const detail = customEvent.detail;
      if (!detail) return;
      if (detail.courseSlug === courseSlug && detail.lessonSlug === lessonSlug) {
        setIsCompleted(true);
      }
    };

    window.addEventListener(LESSON_PROGRESS_COMPLETED_EVENT, handleCompletion);

    return () => {
      window.removeEventListener(LESSON_PROGRESS_COMPLETED_EVENT, handleCompletion);
    };
  }, [courseSlug, lessonSlug]);

  const isUnlocked = isCompleted || !isAuthenticated;

  return (
    <div className="rounded-2xl border border-border/60 bg-background p-6 shadow-sm">
      <h3 className="text-sm font-semibold text-foreground">Lesson navigation</h3>
      <div className="mt-4 flex flex-col gap-2 text-sm">
        {prevTrackGroup ? (
          <div className="space-y-1">
            <Button asChild variant="outline" className="w-full">
              <Link href={`/courses/${courseSlug}/tracks/${prevTrackGroup.id}`}>
                ← Back to {prevTrackGroup.title}
              </Link>
            </Button>
            <p className="px-1 text-xs text-muted-foreground">
              Complete another track or review your progress
            </p>
          </div>
        ) : prevLesson ? (
          <div className="space-y-1">
            <Button asChild variant="outline" className="w-full">
              <Link href={`/courses/${courseSlug}/lessons/${prevLesson.slug}`}>
                ← Previous: {prevLesson.title}
              </Link>
            </Button>
            {prevModuleTitle && (
              <p className="px-1 text-xs text-muted-foreground">from {prevModuleTitle}</p>
            )}
          </div>
        ) : null}

        {/* Don't show next lesson if there's a universal assessment or a track group to choose - user must interact with it first */}
        {nextLesson && !shouldHideNextLesson && !nextUniversalAssessment && !(!nextLessonInModule && nextTrackGroup) && (
          <div className="space-y-1">
            {isUnlocked ? (
              <Button asChild variant="default" className="w-full">
                <Link href={`/courses/${courseSlug}/lessons/${nextLesson.slug}`}>
                  Next: {nextLesson.title} →
                </Link>
              </Button>
            ) : (
              <Button variant="outline" disabled className="w-full cursor-not-allowed">
                Next: {nextLesson.title} →
                <Lock className="ml-2 h-3 w-3" />
              </Button>
            )}
            {nextModuleTitle && (
              <p className="px-1 text-xs text-muted-foreground">in {nextModuleTitle}</p>
            )}
            {!isUnlocked && isAuthenticated && (
              <p className="px-1 text-xs text-muted-foreground">Complete this lesson to unlock</p>
            )}
          </div>
        )}

        {/* Universal assessments take priority over module assessments and next lesson */}
        {!nextLessonInModule && nextUniversalAssessment && (
          <div className="space-y-2">
            {isUnlocked ? (
              <>
                <Button asChild variant="default" className="w-full gap-2">
                  <Link href={`/courses/${courseSlug}/universal-assessments/${nextUniversalAssessment.slug}`}>
                    {nextUniversalAssessment.completed ? "✓ Review: " : "⚠️ Required: "}
                    {nextUniversalAssessment.title} →
                  </Link>
                </Button>
                {!nextUniversalAssessment.completed && (
                  <p className="px-1 text-xs font-medium text-orange-600 dark:text-orange-400">
                    Complete this assessment to continue to the next module
                  </p>
                )}
                {nextUniversalAssessment.completed && (
                  <p className="px-1 text-xs font-medium text-green-600 dark:text-green-400">
                    ✓ Completed! You can review or retake if needed
                  </p>
                )}
              </>
            ) : (
              <>
                <Button variant="outline" disabled className="w-full cursor-not-allowed">
                  {nextUniversalAssessment.completed ? "✓ Review: " : "⚠️ Required: "}
                  {nextUniversalAssessment.title} →
                  <Lock className="ml-2 h-3 w-3" />
                </Button>
                {isAuthenticated && (
                  <p className="px-1 text-xs text-muted-foreground">Complete this lesson first</p>
                )}
              </>
            )}
          </div>
        )}

        {/* Module assessments (show only if no universal assessments) */}
        {!nextLessonInModule && !nextUniversalAssessment && nextAssessment && nextAssessmentSlug && (
          <div className="space-y-2">
            {isUnlocked ? (
              <>
                <Button asChild variant="default" className="w-full gap-2">
                  <Link href={`/courses/${courseSlug}/assessments/${nextAssessmentSlug}`}>
                    {nextAssessment.required ? "⚠️ Required Assessment: " : "Take Assessment: "}
                    {nextAssessment.title} →
                  </Link>
                </Button>
                {nextAssessment.required && (
                  <p className="px-1 text-xs font-medium text-orange-600 dark:text-orange-400">
                    You must pass this assessment to continue to the next module
                  </p>
                )}
              </>
            ) : (
              <Button variant="outline" disabled className="w-full cursor-not-allowed">
                {nextAssessment.required ? "⚠️ Required Assessment: " : "Take Assessment: "}
                {nextAssessment.title} →
                <Lock className="ml-2 h-3 w-3" />
              </Button>
            )}
            {!isUnlocked && isAuthenticated && (
              <p className="px-1 text-xs text-muted-foreground">Complete this lesson to unlock</p>
            )}
          </div>
        )}

        {/* Track Group Selection (if next module is part of a track group) */}
        {!nextLessonInModule && !nextUniversalAssessment && !nextAssessment && nextTrackGroup && (
          <div className="space-y-2">
            {isUnlocked ? (
              <>
                <Button asChild variant="default" className="w-full gap-2">
                  <Link href={`/courses/${courseSlug}/tracks/${nextTrackGroup.id}`}>
                    Choose your track →
                  </Link>
                </Button>
                <p className="px-1 text-xs text-muted-foreground">
                  Select a learning path in {nextTrackGroup.title} to continue
                </p>
              </>
            ) : (
              <>
                <Button variant="outline" disabled className="w-full cursor-not-allowed">
                  Choose your track →
                  <Lock className="ml-2 h-3 w-3" />
                </Button>
                {isAuthenticated && (
                  <p className="px-1 text-xs text-muted-foreground">Complete this lesson first</p>
                )}
              </>
            )}
          </div>
        )}

        {/* Track Group Navigation (if in a track) */}
        {trackGroup && trackGroup.isCurrentModuleTrack && (
          <div className="space-y-2">
            <Button asChild variant="outline" className="w-full gap-2">
              <Link href={`/courses/${courseSlug}/tracks/${trackGroup.id}`}>
                View all tracks in {trackGroup.title}
              </Link>
            </Button>
            <p className="px-1 text-xs text-muted-foreground">
              Explore other learning paths in this track group
            </p>
          </div>
        )}

        {/* Complete Course button (last lesson of the course) */}
        {isLastLesson && !nextLesson && !nextAssessment && !nextUniversalAssessment && !nextTrackGroup && (
          <div className="space-y-2">
            {isUnlocked ? (
              <>
                <Button asChild variant="default" className="w-full gap-2">
                  <Link href={`/courses/${courseSlug}`}>
                    Complete Course →
                  </Link>
                </Button>
                <p className="px-1 text-xs text-muted-foreground">
                  Finish this lesson to complete the course
                </p>
              </>
            ) : (
              <>
                <Button variant="outline" disabled className="w-full cursor-not-allowed">
                  Complete Course →
                  <Lock className="ml-2 h-3 w-3" />
                </Button>
                {isAuthenticated && (
                  <p className="px-1 text-xs text-muted-foreground">Complete this lesson to finish the course</p>
                )}
              </>
            )}
          </div>
        )}

        <div className="mt-2 border-t border-border/40 pt-2">
          <Button asChild variant="ghost" size="sm" className="w-full">
            <Link href={`/courses/${courseSlug}`}>
              ← Back to course overview
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
